import './Ratings.scss'

import StarsDisplay from '../StarsDisplay'
import DetailedRatingsPopper, {
  DetailedRatingsPopperAPI
} from '../DetailedRatingsPopper'
import {
  InternalComponentAPI,
  InternalPluginProps,
  Maybe,
  UpdatedRatings
} from '../../utils/types'
import { hasRatings } from '../../utils/ratings'
import { isDesktop } from '../../utils/responsive'
import { addEventListener } from '../../utils/events'
import {
  withClass,
  withClasses,
  withSelectorHierarchy
} from '../../utils/selectors'
import { formatNumber } from '../../utils/numberFormat'
import { setChildContent, toggleChildClass } from '../../utils/dom'

const RATING_CLASS = 'ratings'
const HAS_RATINGS_CLASS = 'has-ratings'
const RATING_STARS_CLASS = 'ratings__stars'
const RATING_AVERAGE_CLASS = 'ratings__average-rating'
const RATING_COUNT_VALUE_CLASS = 'ratings__ratings-count-value'
const OPEN_DETAILED_RATINGS_CLASS = 'ratings__detailed-ratings-btn'
const OPEN_DETAILED_RATINGS_DATA_ATTRIB = 'data-detailed-ratings-open'

type RatingsProps = InternalPluginProps & {
  target: Element
  onSubmitRatingClick: (event: Event) => void
  onMobileRatingClick?: (event: Event) => void
}

const Ratings = ({
  target,
  onSubmitRatingClick,
  onMobileRatingClick,
  ...props
}: RatingsProps): InternalComponentAPI => {
  target.innerHTML = ratingsAnchorComponent(props)

  const starsDisplay = StarsDisplay({
    target: target.querySelector(withClass(RATING_STARS_CLASS))!,
    rating: props.itemRatings.averageRating
  })

  const toggleDetailedRatingsPopperArrow = (open: boolean) => {
    toggleChildClass({
      parent: target,
      selector: withClass(OPEN_DETAILED_RATINGS_CLASS),
      className: 'ratings__detailed-ratings-btn--inverse',
      classPresent: open
    })
  }

  let isDetailedRatingsOpen = false
  let detailedRatings: Maybe<DetailedRatingsPopperAPI> = null
  if (isDesktop()) {
    detailedRatings = DetailedRatingsPopper({
      ...props,
      dialogParent: document.body,
      anchor: target,
      onPopoverStateChange: (open: boolean) => {
        isDetailedRatingsOpen = open
        toggleDetailedRatingsPopperArrow(open)
      },
      onSubmitRatingClick
    })
    // Open detailed ratings when clicking on stars (only if there are ratings)
    addEventListener({
      target,
      eventName: 'click',
      selector: withSelectorHierarchy([
        withClasses([RATING_CLASS, HAS_RATINGS_CLASS]),
        '*'
      ]),
      handler: () => {
        isDetailedRatingsOpen
          ? detailedRatings?.hide()
          : detailedRatings?.show()
      }
    })
  } else {
    // Execute callback when clicking on stars on mobile (only if there are ratings)
    if (onMobileRatingClick) {
      addEventListener({
        target,
        eventName: 'click',
        selector: withSelectorHierarchy([
          withClasses([RATING_CLASS, HAS_RATINGS_CLASS]),
          '*'
        ]),
        handler: onMobileRatingClick
      })
    }
  }

  const updateRatings = (updatedRatings: UpdatedRatings) => {
    starsDisplay.updateRating(updatedRatings.itemRatings.averageRating)
    detailedRatings?.updateRatings(updatedRatings)
    target
      .querySelector(withClass(RATING_CLASS))
      ?.classList.add(HAS_RATINGS_CLASS)
    setChildContent({
      parent: target,
      selector: withClass(RATING_AVERAGE_CLASS),
      html: formatNumber(updatedRatings.itemRatings.averageRating)
    })
    setChildContent({
      parent: target,
      selector: withClass(RATING_COUNT_VALUE_CLASS),
      html: formatNumber(updatedRatings.itemRatings.numberOfRatings)
    })
  }

  return {
    updateRatings,
    destroy: () => {
      detailedRatings?.destroy()
      target.innerHTML = ''
    }
  }
}

export default Ratings

//
// Templates
//

const ratingsAnchorComponent = (props: InternalPluginProps) => `
  <div class="${RATING_CLASS} ${
  hasRatings(props.itemRatings) ? HAS_RATINGS_CLASS : ''
}">
    <div class="${RATING_STARS_CLASS}"></div>
    ${RatingsExtra(props)}
  </div>
`

const RatingsExtra = ({ itemRatings, translations }: InternalPluginProps) => `
  <div>
    <div class="ratings__extra">
      <div class="${RATING_AVERAGE_CLASS}">
        ${formatNumber(itemRatings.averageRating)}
      </div>
      <div class="ratings__ratings-count">
        <span class="${RATING_COUNT_VALUE_CLASS}">
          ${formatNumber(itemRatings.numberOfRatings)}
        </span>
        <span class="ratings__ratings-count-label">
          ${translations.ratings}
        </span>
      </div>
      <button
        class="${OPEN_DETAILED_RATINGS_CLASS}"
        aria-label="Open detailed ratings"
        ${OPEN_DETAILED_RATINGS_DATA_ATTRIB}
      ></button>
    </div>
    <div class="ratings__not-enough-ratings">
      ${translations.notEnoughRatings}
    </div>
  </div>
`
